/**
 * Billing plan definitions and helpers for Stripe integration.
 *
 * NEW TIER MODEL (March 2026):
 * - Tiers are fixed bundles — no mix-and-match.
 * - You buy a tier, you get what's in it.
 *
 * Residential tiers:
 *   Essentials ($9/$10)          → Compliance Essentials
 *   Professional ($14/$16)       → + Workforce & Operations
 *   Professional+Clinical ($19/$22) → + Clinical Care Suite
 *   Enterprise ($24/$27)         → All packages incl. Financial
 *
 * Home Care tiers:
 *   Compliance Essentials ($10/$12) → Compliance only
 *   HC Essentials ($12/$14)      → Compliance + SAH modules
 *   HC Professional ($17/$19)    → + Workforce
 *   HC Complete ($24/$27)        → All packages including Clinical
 *
 * Stripe Products & Prices must be created with the lookup_keys defined here.
 * Run `pnpm stripe:seed` (or create manually in Stripe Dashboard) before first use.
 */

import {
  COMPLIANCE_TRIAL_SCOPE_MARKER,
  isComplianceTrialRouteAllowed,
} from './compliance-trial-access';

export { COMPLIANCE_TRIAL_SCOPE_MARKER } from './compliance-trial-access';

/* ── Plan definitions ──────────────────────────────────────── */

export type BillingPlanKey =
  | 'residential_essentials'
  | 'residential_professional'
  | 'residential_professional_clinical'
  | 'residential_enterprise'
  | 'home_care_compliance'
  | 'home_care_essentials'
  | 'home_care_professional'
  | 'home_care_complete';

/** @deprecated Use BillingPlanKey — kept for legacy tenant migration */
export type LegacyBillingPlanKey =
  | 'residential_starter'
  | 'residential_professional'
  | 'residential_enterprise'
  | 'home_care'
  | 'home_care_plus';

export type BillingInterval = 'monthly' | 'annual';
export type ModuleOrgType = 'HOME_CARE' | 'RESIDENTIAL' | 'MIXED';

export type SubscriptionStatus =
  | 'trialing'
  | 'trial_expired'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'suspended'
  | 'incomplete'
  | 'unpaid';

export interface BillingPlan {
  key: BillingPlanKey;
  name: string;
  careType: 'residential' | 'home_care';
  monthlyPriceCents: number; // AUD cents per unit
  annualPriceCents: number; // AUD cents per unit (per month equivalent)
  unitLabel: 'bed' | 'client';
  packages: string[]; // package keys included in this tier
  maxFacilities: number; // -1 = unlimited
  maxUsers: number; // -1 = unlimited
  requiresGuidedOnboarding: boolean;
  includesFamilyPortal: boolean;
  includesCareWorkerApp: boolean;
  stripeLookupKey: (interval: BillingInterval) => string;
}

export const BILLING_PLANS: Record<BillingPlanKey, BillingPlan> = {
  /* ── Residential tiers ────────────────────────────────────── */
  residential_essentials: {
    key: 'residential_essentials',
    name: 'Essentials',
    careType: 'residential',
    monthlyPriceCents: 1000,
    annualPriceCents: 900,
    unitLabel: 'bed',
    packages: ['compliance_essentials'],
    maxFacilities: 3,
    maxUsers: -1,
    requiresGuidedOnboarding: false,
    includesFamilyPortal: false,
    includesCareWorkerApp: false,
    stripeLookupKey: (i) => `residential_essentials_${i}`,
  },
  residential_professional: {
    key: 'residential_professional',
    name: 'Professional',
    careType: 'residential',
    monthlyPriceCents: 1600,
    annualPriceCents: 1400,
    unitLabel: 'bed',
    packages: ['compliance_essentials', 'workforce_operations'],
    maxFacilities: 5,
    maxUsers: -1,
    requiresGuidedOnboarding: false,
    includesFamilyPortal: false,
    includesCareWorkerApp: false,
    stripeLookupKey: (i) => `residential_professional_${i}`,
  },
  residential_professional_clinical: {
    key: 'residential_professional_clinical',
    name: 'Professional + Clinical',
    careType: 'residential',
    monthlyPriceCents: 2200,
    annualPriceCents: 1900,
    unitLabel: 'bed',
    packages: ['compliance_essentials', 'workforce_operations', 'clinical_care'],
    maxFacilities: 10,
    maxUsers: -1,
    requiresGuidedOnboarding: true,
    includesFamilyPortal: true,
    includesCareWorkerApp: true,
    stripeLookupKey: (i) => `residential_professional_clinical_${i}`,
  },
  residential_enterprise: {
    key: 'residential_enterprise',
    name: 'Enterprise',
    careType: 'residential',
    monthlyPriceCents: 2700,
    annualPriceCents: 2400,
    unitLabel: 'bed',
    packages: [
      'compliance_essentials',
      'workforce_operations',
      'clinical_care',
      'financial_management',
    ],
    maxFacilities: -1,
    maxUsers: -1,
    requiresGuidedOnboarding: true,
    includesFamilyPortal: true,
    includesCareWorkerApp: true,
    stripeLookupKey: (i) => `residential_enterprise_${i}`,
  },

  /* ── Home Care tiers ──────────────────────────────────────── */
  home_care_compliance: {
    key: 'home_care_compliance',
    name: 'Compliance Essentials (Home Care)',
    careType: 'home_care',
    monthlyPriceCents: 1200,
    annualPriceCents: 1000,
    unitLabel: 'client',
    packages: ['compliance_essentials'],
    maxFacilities: 3,
    maxUsers: -1,
    requiresGuidedOnboarding: false,
    includesFamilyPortal: false,
    includesCareWorkerApp: false,
    stripeLookupKey: (i) => `home_care_compliance_${i}`,
  },
  home_care_essentials: {
    key: 'home_care_essentials',
    name: 'Home Care Essentials',
    careType: 'home_care',
    monthlyPriceCents: 1400,
    annualPriceCents: 1200,
    unitLabel: 'client',
    // HC Essentials marketing: compliance + SAH only, NOT full finance.
    // sah_funding covers Support at Home modules without unlocking /finance/*.
    packages: ['compliance_essentials', 'sah_funding'],
    maxFacilities: 3,
    maxUsers: -1,
    requiresGuidedOnboarding: false,
    includesFamilyPortal: false,
    includesCareWorkerApp: false,
    stripeLookupKey: (i) => `home_care_essentials_${i}`,
  },
  home_care_professional: {
    key: 'home_care_professional',
    name: 'Home Care Professional',
    careType: 'home_care',
    monthlyPriceCents: 1900,
    annualPriceCents: 1700,
    unitLabel: 'client',
    // Professional adds workforce; still no full finance module.
    packages: ['compliance_essentials', 'workforce_operations', 'sah_funding'],
    maxFacilities: 5,
    maxUsers: -1,
    requiresGuidedOnboarding: false,
    includesFamilyPortal: false,
    includesCareWorkerApp: true,
    stripeLookupKey: (i) => `home_care_professional_${i}`,
  },
  home_care_complete: {
    key: 'home_care_complete',
    name: 'Home Care Complete',
    careType: 'home_care',
    monthlyPriceCents: 2700,
    annualPriceCents: 2400,
    unitLabel: 'client',
    packages: [
      'compliance_essentials',
      'workforce_operations',
      'clinical_care',
      'financial_management',
      'sah_funding',
    ],
    maxFacilities: -1,
    maxUsers: -1,
    requiresGuidedOnboarding: true,
    includesFamilyPortal: true,
    includesCareWorkerApp: true,
    stripeLookupKey: (i) => `home_care_complete_${i}`,
  },
};

/** True only when the billing catalogue contains compliance essentials and no operational package. */
export function isComplianceOnlyPlan(billingPlan: string | null | undefined): boolean {
  if (!billingPlan || !(billingPlan in BILLING_PLANS)) return false;
  const packages = BILLING_PLANS[billingPlan as BillingPlanKey].packages;
  return packages.length === 1 && packages[0] === 'compliance_essentials';
}

/**
 * Map legacy billing plan keys to new keys.
 * Used during tenant config loading for backward compatibility.
 */
export const LEGACY_PLAN_MAP: Record<string, BillingPlanKey> = {
  residential_starter: 'residential_essentials',
  home_care: 'home_care_essentials',
  home_care_plus: 'home_care_professional',
  // residential_professional and residential_enterprise keys are reused
};

const HOME_CARE_COMPLIANCE_EXCLUDED_MODULE_PREFIXES = [
  '/people/learning',
  '/hr/training',
  '/staff/training',
  '/me/training',
  '/compliance/training-delivery',
] as const;

function applyHomeCareComplianceCeiling(modules: string[], plan: BillingPlanKey): string[] {
  if (plan !== 'home_care_compliance') return modules;
  return modules.filter(
    (modulePath) =>
      !HOME_CARE_COMPLIANCE_EXCLUDED_MODULE_PREFIXES.some(
        (prefix) => modulePath === prefix || modulePath.startsWith(`${prefix}/`),
      ),
  );
}

const COMPLIANCE_ONLY_PLAN_MODULES = [
  '/compliance/ipc',
  '/compliance/ipc/outbreaks',
  '/compliance/ipc/surveillance',
  '/compliance/ipc/reports',
  '/compliance/whs',
  '/compliance/whs/incidents',
  '/compliance/whs/hazards',
  '/compliance/whs/chemicals',
  '/compliance/whs/equipment',
  '/compliance/whs/psychosocial',
  '/compliance/whs/drills',
] as const;

function addComplianceOnlyPlanModules(modules: Set<string>, plan: BillingPlanKey): void {
  if (!isComplianceOnlyPlan(plan)) return;
  for (const modulePath of COMPLIANCE_ONLY_PLAN_MODULES) modules.add(modulePath);
}

export const TIER_TO_BILLING_PLAN: Record<string, BillingPlanKey> = {
  essentials: 'residential_essentials',
  professional: 'residential_professional',
  professional_clinical: 'residential_professional_clinical',
  enterprise: 'residential_enterprise',
  home_care_compliance: 'home_care_compliance',
  home_care_essentials: 'home_care_essentials',
  home_care_professional: 'home_care_professional',
  home_care_complete: 'home_care_complete',
  starter: 'residential_essentials',
  standard: 'residential_essentials',
  home_care: 'home_care_essentials',
  home_care_plus: 'home_care_professional',
};

const RESIDENTIAL_ONLY_MODULE_PREFIXES = [
  '/residential',
  '/compliance/rn-coverage',
  '/compliance/prudential',
  '/finance/an-acc',
  '/finance/residential-billing',
  '/finance/respite-billing',
  // Residential accommodation (RAD/DAP/MPIR). Also added as a member of the
  // financial_management package below so it is a KNOWN module boundary —
  // otherwise the bare `/finance` grant would auto-satisfy access and this
  // strip would be a no-op for HOME_CARE Complete tenants.
  '/finance/accommodation',
  '/dashboards/don-clinical',
  '/dashboards/facility-ops',
  '/dashboards/multi-facility',
  // RAC operations consoles (RacOperationsNav + Rac*Panel). These live in the
  // shared compliance_essentials / workforce_operations packages but render
  // residential-only content + links, so strip them for HOME_CARE. SAH tenants
  // use /dashboards/compliance and /dashboards/home-care-ops instead.
  '/dashboards/workforce',
  '/dashboards/quality',
] as const;

const HOME_CARE_ONLY_MODULE_PREFIXES = [
  '/home-care',
  '/clients/*/sah',
  '/compliance/sah-evidence',
  '/finance/contributions',
  '/finance/budgets/carry-over',
  // SAH operational reports (budget consumption + classification history). Also
  // added to the sah_funding package below so it is a KNOWN module boundary —
  // otherwise the bare `/reports` grant would auto-satisfy access and this
  // strip would be a no-op for RESIDENTIAL tenants on Professional+.
  '/reports/sah',
  '/dashboards/home-care-ops',
] as const;

/* ── Package → Module mapping ──────────────────────────────── */

/**
 * Each package defines the module paths it grants access to.
 *
 * Module placement rationale (April 2026):
 * - /clients: compliance_essentials (foundational entity for incidents/complaints)
 * - /care-plans: clinical_care (care delivery, not compliance)
 * - /compliance/screening, /compliance/certifications, /compliance/workforce: workforce_operations (worker checks and role-required evidence)
 * - /compliance/whs: workforce_operations (workplace health & safety)
 * - /compliance/clinical-governance: clinical_care (clinical oversight, medication safety)
 * - /compliance/restrictive-practices: compliance_essentials (ALL providers must report under Aged Care Act 2024)
 * - /compliance/provider-obligations: compliance_essentials (New Act obligation evidence spine)
 * - /compliance/audit-pack: compliance_essentials (New Act recordkeeping/export evidence)
 * - /compliance/ipc: clinical_care (infection prevention is clinical)
 * - /compliance/prudential: financial_management entitlement; provider-entity facts
 *   determine applicability, including category 4/5 SAH providers. Residential
 *   accommodation finance remains separately gated by its own routes.
 * - /compliance/sah-evidence: sah_funding (regional compliance evidence review for SAH)
 * - /clinical: clinical_care landing route (redirects into clinical workspace)
 * - Client-detail clinical overlays: clinical_care. Base /clients entitlement
 *   must not unlock PHI-bearing clinical child routes.
 * - /dashboards/don-clinical: clinical_care + residential org type
 * - /residential/activities,visitors,community,night-rounds,meal-management,rounds,adl-events: workforce_operations (daily ops)
 * - /residential/rooms,admissions,occupancy,care-minutes: financial_management (tied to billing)
 * - /residential/dap-accrual: financial_management (residential accommodation payment accrual)
 * - /finance/contributions, /home-care/statements,claims,at-hm: sah_funding (SAH operational workflows without full finance)
 * - /availability, /me/roster,timesheets,leave,training: workforce_operations (personal workspace needs workforce)
 * - /waitlist: compliance_essentials (client intake pipeline)
 * - /analytics/board-pack,benchmarking: financial_management (premium executive reporting)
 */
export const PACKAGE_MODULES: Record<string, string[]> = {
  compliance_essentials: [
    // Resident/Client Registry — foundational entity for incident & complaint context
    '/clients',
    // Client intake is a distinct module boundary from the registry list surface.
    '/clients/new',
    // Core compliance modules
    '/compliance/incidents',
    // Open disclosure — SIRS/incident closure precondition (BA journey step)
    '/compliance/open-disclosure',
    '/compliance/quality-indicators',
    '/compliance/standards',
    '/compliance/quality/feedback',
    '/compliance/audit',
    '/compliance/audit-program',
    '/compliance/audit-trail',
    '/compliance/audit-pack',
    '/compliance/audit-pack/export',
    '/compliance/governance',
    '/compliance/policies',
    '/people/policies-conduct',
    '/compliance/environmental',
    '/compliance/code-of-conduct',
    // CoC hub sub-surfaces — entitled with the hub so a standalone
    // compliance_essentials tenant doesn't dead-end on them.
    '/compliance/code-of-conduct/analytics',
    '/compliance/code-of-conduct/investigations',
    '/compliance/code-of-conduct/reports',
    '/compliance/complaints',
    '/compliance/whistleblower',
    '/compliance/data-breach',
    '/compliance/star-ratings',
    '/compliance/responsible-persons',
    '/compliance/governance/risk-register',
    '/compliance/governance/skills-matrix',
    '/compliance/governance/conflicts',
    '/compliance/continuous-improvement',
    '/compliance/continuous-improvement?view=dashboard',
    '/compliance/continuous-improvement/new',
    '/compliance/continuous-improvement?view=actions',
    // Wave 8/13 compliance-only surfaces. Keep concrete entries so these
    // module boundaries cannot rely on an incidental parent-prefix grant.
    '/compliance/work',
    '/compliance/evidence-packs',
    '/compliance/governance/qcab',
    '/compliance/workforce/import',
    '/compliance/documents',
    '/compliance/documents/expiring',
    '/compliance/registration',
    '/compliance/provider-obligations',
    '/compliance/star-ratings/methodology',
    '/compliance/reporting',
    // Restrictive practices — ALL providers must report under Aged Care Act 2024
    '/compliance/restrictive-practices',
    // Compliance dashboards
    '/dashboards/compliance',
    '/dashboards/quality',
    '/compliance/calendar?view=agenda',
    // The gate matches the pathname without its query, so the calendar page
    // itself must be granted for the Deadlines agenda link to open.
    '/compliance/calendar',
    '/settings/compliance',
  ],

  workforce_operations: [
    // Waitlist — client intake pipeline (operational, not compliance)
    '/waitlist',
    // Personal workspace views — roster, timesheets, leave, hours, training and worker lifecycle
    '/availability',
    '/me/availability',
    '/me/roster',
    '/me/shift-offers',
    '/me/shift-swaps',
    '/me/timesheets',
    '/me/messages',
    '/me/leave',
    '/me/hours',
    '/me/preferences',
    '/me/renewals',
    '/me/training',
    '/me/courses',
    '/me/compliance',
    '/me/capability',
    '/me/performance',
    '/me/policies',
    '/me/onboarding',
    '/me/casual-conversion',
    '/me/supervision',
    '/me/contractor',
    '/me/contractor/compliance',
    '/me/contractor/invoice',
    '/me/contractor/invoices',
    '/me/contractor/log',
    '/me/contractor/profile',
    '/me/contractor/shifts',
    // Analytics dashboard & reports builder (professional+ tier)
    '/analytics',
    '/analytics/reports',
    '/reports',
    '/reports/subscriptions',
    '/reports/workforce/recalls',
    // Workforce management
    '/staff',
    '/roster',
    '/roster/board',
    '/roster/dispatch',
    '/roster/grid',
    '/contractors',
    '/staff/training',
    '/workforce/roster',
    '/workforce/capabilities',
    '/workforce/availability',
    '/workforce/handovers',
    '/workforce/mobile-activity',
    '/workforce/timesheets/disputes',
    '/roster/open-shifts',
    '/roster/periods',
    '/roster/import',
    '/roster/templates',
    '/roster/timesheets',
    '/roster/today',
    '/roster/leave',
    '/roster/shift-swaps',
    '/roster/handovers',
    '/roster/recalls',
    '/roster/recalls/new',
    '/roster/sleepover-disturbances',
    '/roster/sleepover-disturbances/new',
    // D9 (2026-08-25) grant half. Without this a packaged RAC tenant cannot
    // reach its own staffing-demand, roster-health or proposal surfaces at all.
    //
    // These are sold to every workforce_operations tenant, home care included,
    // and no /roster path is in RESIDENTIAL_ONLY_MODULE_PREFIXES — so the
    // entitlement layer does NOT narrow them by operating model. Each surface
    // carries its own runtime gate instead, and they are three different gates:
    //   /roster/wing-staffing  RCA-1.8 (a nav segment; refuses HOME_CARE in
    //                          wing-staffing/page.tsx before any query)
    //   /roster/health         deliberately mixed — showHC/showRAC in
    //                          RosterHealthPage; RAC drill-ins carry
    //                          guardRosterCohort('RESIDENTIAL')
    //   /roster/proposals/*    refuseProposalOnMode — RCA-1.8 covers nav
    //                          segments only, and these are CONTEXTUAL routes
    // Do not describe this as "gated by RCA-1.8": only the first one is.
    '/roster/health',
    '/roster/wing-staffing',
    '/roster/proposals',
    // Worker screening — police checks, NDIS worker screening
    '/compliance/screening',
    '/compliance/certifications',
    '/compliance/workforce',
    '/compliance/capability-framework',
    '/compliance/training-delivery',
    '/compliance/validity-rules',
    // WHS — workplace health & safety is a workforce function
    '/compliance/whs',
    '/compliance/whs/incidents',
    '/compliance/whs/hazards',
    '/compliance/whs/chemicals',
    '/compliance/whs/equipment',
    '/compliance/whs/psychosocial',
    '/compliance/whs/drills',
    // Daily residential operations managed by staff
    '/residential',
    '/residential/activities',
    '/residential/visitors',
    '/residential/community',
    '/residential/night-rounds',
    '/residential/rounds',
    '/residential/adl-events',
    '/residential/meal-management',
    '/residential/daily-living',
    // Payroll (SCHADS Award MA000100 pay-run lifecycle is the headline
    // workforce-operations sale; the parent layout must be entitled as well
    // as child pages because payroll routes call requireModule('/payroll')).
    '/payroll',
    '/payroll/readiness',
    '/payroll/setup',
    '/payroll/manual-closures',
    '/payroll/provider-handoff',
    '/payroll/fixed-pay',
    '/payroll/corrections',
    '/payroll/policy',
    '/payroll/queue',
    '/payroll/pay-runs',
    '/payroll/timesheet',
    '/payroll/reconciliation',
    '/payroll/award-review',
    '/payroll/agency-invoices',
    '/settings/payroll/aba-sender',
    '/settings/payroll/eba',
    '/settings/payroll/pay-roles',
    '/settings/integrations',
    '/settings/integrations/xero',
    // HR — workforce-management workflows shipped behind workforce_operations.
    // Roles already mapped in route-access.ts; the package-membership gap
    // surfaced as "Module Not Available" for paying Pro/Pro+Clinical tenants
    // (Phase 7.5 P1-D). Only sub-routes with implementing pages.
    '/hr/onboarding',
    '/hr/offboarding',
    '/hr/compliance',
    '/hr/casual-conversion',
    '/hr/training',
    '/people/learning',
    '/people/learning/courses',
    '/people/learning/external-evidence',
    '/people/learning/competencies',
    '/people/development',
    '/people/development/performance',
    '/people/development/supervision',
    '/people/evidence',
    '/hr/performance',
    '/hr/performance/pips',
    '/hr/clinical-supervision',
    '/hr/policies',
    '/people/policies-conduct',
    '/hr/workforce-obligations',
    // Workforce dashboards
    '/dashboards/workforce',
    '/dashboards/huddle',
  ],

  clinical_care: [
    // Care delivery
    '/clinical',
    '/clients/*/clinical',
    '/clients/*/devices',
    '/clients/*/medications',
    '/clients/*/observations',
    '/clients/*/clinical/pathways',
    '/clients/*/clinical/restorative',
    '/clinical/alerts',
    '/clinical/escalations',
    '/clinical/medication-incidents',
    '/clinical/pathways',
    '/clinical/restorative',
    '/clinical/supervision',
    '/clinical/wounds',
    '/care-plans',
    '/assessments',
    '/observations',
    '/clinical/handovers',
    '/clinical/overdue-assessments',
    '/medications',
    '/medications/mar',
    '/medications/s8-register',
    '/wounds',
    '/advance-care-plans',
    '/care-pathways',
    '/programs',
    '/telehealth',
    '/telehealth/schedule',
    '/telehealth/recordings',
    '/monitoring',
    '/monitoring/devices',
    '/monitoring/vital-signs',
    '/monitoring/alerts',
    '/monitoring/equipment',
    // Clinical compliance — requires clinical context to manage
    '/compliance/clinical-governance',
    '/compliance/rn-coverage',
    '/compliance/ipc',
    '/compliance/ipc/outbreaks',
    '/compliance/ipc/surveillance',
    '/compliance/ipc/reports',
    // Clinical dashboards
    '/dashboards/don-clinical',
    '/dashboards/caseload',
  ],

  financial_management: [
    // Premium analytics — board-level reporting & benchmarking
    '/analytics/board-pack',
    '/analytics/benchmarking',
    // Core financial
    '/finance',
    '/finance/invoices',
    '/finance/claims',
    '/finance/reports',
    // Overnight provider-managed billing build (2026-08-18). These four
    // become KNOWN module boundaries the moment they're listed here (see the
    // `/finance/accommodation` comment below for the mechanism), but every
    // financial_management tenant already carries bare `/finance` in this
    // same array, so nothing loses access — this only makes the grant
    // explicit for the reverse-mapping test and future org-type filtering.
    '/finance/billing',
    '/finance/billing/readiness',
    '/finance/payers',
    '/finance/charges',
    '/finance/statements',
    '/pricing/rate-cards',
    '/pricing/agreements',
    '/finance/an-acc',
    '/finance/reports/statements',
    '/finance/reports/aged-debtors',
    '/finance/reports/reconciliation',
    '/finance/residential-billing',
    '/finance/respite-billing',
    // Finance-owned integrations and accounting configuration.
    '/settings/integrations',
    '/settings/integrations/xero',
    // Prudential — provider-entity financial governance. Entitlement remains
    // financial_management; QI/FPS facts decide which surfaces apply.
    '/compliance/prudential',
    '/compliance/prudential/deposits',
    // Accommodation — tied to billing, RADs, accommodation payments.
    // Membership here makes /finance/accommodation a KNOWN boundary so the bare
    // `/finance` grant no longer auto-grants it; org-type filtering
    // (RESIDENTIAL_ONLY_MODULE_PREFIXES) then strips it for HOME_CARE.
    '/finance/accommodation',
    '/residential/rooms',
    '/residential/admissions',
    '/residential/occupancy',
    '/residential/care-minutes',
    '/residential/dap-accrual',
    '/residential/care-minutes/an-acc',
    '/residential/care-minutes/daily',
    // Financial & ops dashboards
    '/dashboards/finance',
    '/dashboards/facility-ops',
    '/dashboards/home-care-ops',
    // Enterprise-level dashboards (require financial data context)
    '/dashboards/executive',
    '/dashboards/operations',
    '/dashboards/multi-facility',
    '/dashboards/risk-correlation',
    '/dashboards/iot',
    // Analytics sub-routes
    '/analytics/facilities',
  ],

  // Support at Home — standalone package so HC Essentials/Professional can
  // deliver SAH services without unlocking the full finance suite (BUG-8).
  sah_funding: [
    '/home-care',
    '/clients/*/sah',
    '/cm-tasks',
    '/cm-tasks/new',
    '/cm-tasks/approve',
    '/cm-tasks/unknown',
    '/home-care/service-areas',
    '/home-care/schedules',
    '/home-care/assessments',
    '/home-care/classifications',
    '/home-care/service-delivery',
    '/home-care/budgets',
    '/home-care/funding-changes',
    '/home-care/budget-explainer',
    '/home-care/readiness',
    '/home-care/statements',
    '/home-care/claims',
    '/home-care/at-hm',
    '/home-care/exceptions',
    '/home-care/reconciliation',
    '/compliance/sah-evidence',
    '/settings/integrations/services-australia',
    // SAH contributions tracking — participant co-payment calculation
    '/finance/contributions',
    '/finance/budgets/carry-over',
    // SAH operational reports (budget consumption + classification history).
    // Membership here makes /reports/sah a KNOWN boundary so the bare `/reports`
    // grant no longer auto-grants it; org-type filtering
    // (HOME_CARE_ONLY_MODULE_PREFIXES) then strips it for RESIDENTIAL.
    '/reports/sah',
    // Home-care operational dashboard
    '/dashboards/home-care-ops',
  ],
};

/** @deprecated Alias for backward compatibility — maps legacy package keys */
export const LEGACY_PACKAGE_ALIAS: Record<string, string> = {
  support_at_home: 'sah_funding',
};

/**
 * Wave 5 — standalone Compliance (Essentials) fine-grained capability keys.
 *
 * `compliance_essentials` is force-added to every tenant's package set (see
 * `addPackageModules` below), so a literal route string placed directly in
 * `PACKAGE_MODULES.compliance_essentials` would appear in the resolved
 * module array of EVERY plan, not only Essentials. For a plan that already
 * carries the wider owning package (`workforce_operations`, `clinical_care`,
 * `financial_management`) that would be a functional no-op — the route is
 * already granted there — but it would still change that plan's resolved
 * module ARRAY, which the plan-matrix regression test in
 * `compliance-w5-plan-matrix.test.ts` pins byte-identical for every
 * non-Essentials plan.
 *
 * Each capability key below is instead granted ADDITIVELY, computed from the
 * tenant's actual resolved package set: a definition's `routes` are added
 * only when the tenant carries `compliance_essentials` and carries NONE of
 * `impliedByPackages`. A tenant that already has the wider package needs no
 * addition (the routes already exist there, or resolve through an existing
 * `module-access.ts` alias); an Essentials-only tenant (including
 * `home_care_essentials`, which never carries `workforce_operations`,
 * `clinical_care` or `financial_management`) gains exactly these routes and
 * nothing else. See `addComplianceCapabilityModules` and
 * `docs/source-of-truth/access-control-and-module-gating.md`.
 *
 * `/people/compliance`, `/people/policies-conduct`, `/dashboards/compliance`,
 * `/compliance/calendar?view=agenda`, `/dashboards/quality`, and the
 * `/settings/compliance-foundation` cluster are documented here for
 * completeness but need no `routes`: `/people/compliance` is already
 * alias-resolved (`module-access.ts` MODULE_ACCESS_ALIASES) onto
 * `/compliance/workforce`, which this table grants; the others are already
 * literal entries in `PACKAGE_MODULES.compliance_essentials` or
 * `PLATFORM_MODULES` and therefore already reach every plan, Essentials
 * included.
 */
export type ComplianceCapabilityKey =
  | '/people/compliance'
  | '/people/learning'
  | '/people/policies-conduct'
  | '/me/compliance'
  | '/compliance/workforce'
  | '/compliance/whs'
  | '/compliance/clinical-governance'
  | '/compliance/rn-coverage'
  | '/compliance/ipc'
  | '/compliance/prudential'
  | '/dashboards/compliance'
  | '/settings/compliance-foundation';

export type ComplianceCapabilityDefinition = {
  /** Routes newly granted for a compliance_essentials-only tenant. */
  routes: readonly string[];
  /**
   * Non-compliance_essentials packages that already imply this capability's
   * routes for tenants that carry them.
   */
  impliedByPackages: readonly string[];
};

/**
 * Packages whose presence means a tenant is on a Professional/Enterprise
 * (or equivalent) tier rather than a genuine compliance_essentials-only
 * Essentials tenant. See `addComplianceCapabilityModules`.
 */
export const COMPLIANCE_CAPABILITY_WIDER_OPERATIONAL_PACKAGES = [
  'workforce_operations',
  'clinical_care',
  'financial_management',
] as const;

export const COMPLIANCE_CAPABILITY_DEFINITIONS: Record<
  ComplianceCapabilityKey,
  ComplianceCapabilityDefinition
> = {
  '/people/compliance': {
    // Alias-resolved onto /compliance/workforce (module-access.ts); no
    // separate literal route needed.
    routes: [],
    impliedByPackages: ['workforce_operations'],
  },
  '/people/learning': {
    // Wave 6 slice 3: Lite workers provide the canonical staff projection
    // used by the learning pages; no HR, payroll, or roster module is needed.
    routes: [
      '/people/learning',
      '/people/learning/courses',
      '/people/learning/external-evidence',
      '/people/learning/competencies',
    ],
    impliedByPackages: ['workforce_operations'],
  },
  '/me/compliance': {
    // Paul decision (1 October 2026): own worker record or staffless guidance.
    routes: ['/me/compliance'],
    impliedByPackages: ['workforce_operations'],
  },
  '/people/policies-conduct': {
    // Wave 5 slice 4 (2026-09-22): DEFERRED from the additive slice. Only
    // the two staff-independent children (governance, policies) are literal
    // PACKAGE_MODULES.compliance_essentials entries now — see
    // ESSENTIALS_CAPABILITIES_DEFERRED_UNTIL_LITE_REGISTERS for the rest.
    routes: [],
    impliedByPackages: [],
  },
  '/compliance/workforce': {
    routes: [
      '/compliance/screening',
      '/compliance/certifications',
      '/compliance/workforce',
      '/compliance/capability-framework',
      '/compliance/training-delivery',
      '/compliance/validity-rules',
    ],
    impliedByPackages: ['workforce_operations'],
  },
  '/compliance/whs': {
    routes: [
      '/compliance/whs',
      '/compliance/whs/incidents',
      '/compliance/whs/hazards',
      '/compliance/whs/chemicals',
      '/compliance/whs/equipment',
      '/compliance/whs/psychosocial',
      '/compliance/whs/drills',
    ],
    impliedByPackages: ['workforce_operations'],
  },
  '/compliance/clinical-governance': {
    routes: ['/compliance/clinical-governance'],
    impliedByPackages: ['clinical_care'],
  },
  '/compliance/rn-coverage': {
    // RESIDENTIAL_ONLY_MODULE_PREFIXES already strips this for HOME_CARE org
    // type after resolution, matching the "RAC only" design requirement.
    routes: ['/compliance/rn-coverage'],
    impliedByPackages: ['clinical_care'],
  },
  '/compliance/ipc': {
    routes: [
      '/compliance/ipc',
      '/compliance/ipc/outbreaks',
      '/compliance/ipc/surveillance',
      '/compliance/ipc/reports',
    ],
    impliedByPackages: ['clinical_care'],
  },
  '/compliance/prudential': {
    // This is a provider-entity surface. R1 BLOCKER (2026-09-21): this is an
    // EXPLICIT CHILD ALLOW-LIST,
    // never the bare parent — module matching is by segment-PREFIX
    // (modulePatternMatches in module-access.ts), so granting the bare
    // '/compliance/prudential' string here would ALSO silently grant every
    // descendant, including /pricing (room pricing) and
    // /accommodation-agreements (agreement financial terms/signed PDFs) —
    // both a financial_management capability, not a compliance register a
    // standalone provider keeps by hand. Deliberately excludes
    // /compliance/prudential (the bare hub — see MODULE_LANDING_PATHS in
    // module-access.ts, which grants the hub itself without cascading to
    // unlisted children), /compliance/prudential/pricing,
    // /compliance/prudential/accommodation-agreements,
    // /finance/accommodation, and any invoice/payment workflow. Any future
    // child not on this list is refused by default (fail closed) — see
    // compliance-w5-essentials-route-matrix.test.ts "unknown child is
    // refused" cases.
    routes: [
      '/compliance/prudential/deposits',
      '/compliance/prudential/governance',
      '/compliance/prudential/reports',
      '/compliance/prudential/liquidity',
      '/compliance/prudential/permitted-uses',
    ],
    impliedByPackages: ['financial_management'],
  },
  '/dashboards/compliance': {
    // /dashboards/compliance, /compliance/calendar?view=agenda, and /dashboards/quality
    // are already literal PACKAGE_MODULES.compliance_essentials entries.
    routes: [],
    impliedByPackages: [],
  },
  '/settings/compliance-foundation': {
    // organisation/facilities/users/registration-providers/document-templates
    // /retention/security/notifications/billing are already PLATFORM_MODULES
    // (platform-exempt, granted regardless of plan).
    routes: [],
    impliedByPackages: [],
  },
};

/**
 * Wave 5 slice 4 (2026-09-22, adversarial review item 4): capability keys
 * that were REACHABLE for a standalone Essentials tenant (either via the
 * Wave 5 slice 1 additive table above, or — for `/people/policies-conduct`'s
 * non-register children — a pre-existing `PACKAGE_MODULES.compliance_
 * essentials` literal) but whose underlying page or RPC needs a worker/staff
 * projection an Essentials tenant does not have. A reachable page that
 * cannot work is exactly what this programme removes, so these are
 * deliberately NOT applied by `addComplianceCapabilityModules` / listed in
 * `PACKAGE_MODULES.compliance_essentials` until a Wave 6 "lite staff
 * register" (a minimal staff/worker projection scoped to Essentials, not the
 * full `workforce_operations` package) exists:
 *
 * - `/people/learning/**` — the learning workbench (courses, external
 *   evidence, competencies) is a per-worker training register; every list
 *   view is keyed by staff/worker id.
 * - `/people/policies-conduct` (the bare hub) and its `assignments`,
 *   `publications`, `code`, `cases` children — all resolve through
 *   `worker_policies_conduct_projection_controlled` (see `./data.ts`'s
 *   `loadPoliciesConductWorkbench`) or take an explicit worker/staff search
 *   parameter. Its `governance` and `policies` children do NOT depend on a
 *   staff projection (both are thin wrappers around the same
 *   `PolicyRegisterPage`/`PolicyLibraryPage` already reachable at
 *   `/compliance/policies`) and remain literal
 *   `PACKAGE_MODULES.compliance_essentials` entries.
 * Paul decision (1 October 2026): `/me/compliance` is enabled for Essentials.
 * It shows the actor's own login-linked staff record; staffless officers get
 * a calm empty state pointing to the People compliance registers. The worker
 * RPCs still require `current_staff_id()`/`staff_facilities`.
 *
 * `/compliance/rn-coverage` was also checked: its exemption-register RPCs
 * (`create_rn_coverage_exemption_controlled`,
 * `delete_rn_coverage_exemption_controlled`,
 * `list_rn_coverage_exemptions_controlled`) never call the residential
 * program-authority registry at all, so they already work. Its daily
 * coverage-status RPC
 * (`list_facility_rn_daily_coverage_status_controlled`) IS registered
 * `ACTIVE_RAC` and is roster-derived, so it stays refused for an Essentials
 * tenant — but the page (`compliance/rn-coverage/page.tsx`) now degrades
 * that refusal to a calm inline note ("Daily coverage status needs Statura
 * rostering") instead of a raw toast, and the exemption register underneath
 * it stays fully usable. `/compliance/rn-coverage` therefore stays entitled,
 * not deferred.
 */
export const ESSENTIALS_CAPABILITIES_DEFERRED_UNTIL_LITE_REGISTERS = [
  '/people/policies-conduct',
  '/people/policies-conduct/assignments',
  '/people/policies-conduct/publications',
  '/people/policies-conduct/code',
  '/people/policies-conduct/cases',
] as const;

/**
 * The three billing plans the Wave 5 additive compliance capability slice
 * attaches to. Deliberately a closed set of exact plan keys, not a package
 * heuristic — see `addComplianceCapabilityModules`.
 */
const COMPLIANCE_CAPABILITY_ESSENTIALS_PLAN_KEYS: ReadonlySet<BillingPlanKey> = new Set([
  'residential_essentials',
  'home_care_essentials',
  'home_care_compliance',
]);

/**
 * Add the Wave 5 compliance capability routes a tenant is entitled to.
 *
 * Gated FIRST on the tenant's AUTHORITATIVE EFFECTIVE BILLING PLAN being
 * one of the three explicit Essentials plan keys — the same
 * `resolvePlanKey()`-resolved plan every other branch of
 * `getEnabledModules`/`getResolvedEnabledModules` treats as authoritative,
 * which is itself fed from `resolveEffectiveBillingPlan()` upstream. A null
 * plan (package-only / legacy / admin-created tenant, e.g.
 * `selectedPackages: ['support_at_home']` normalising to `sah_funding`, or
 * an unknown legacy package) gets NOTHING additional, even if its package
 * array happens to carry none of the wider operational packages below —
 * R1 HIGH (2026-09-21) found that package-shape alone let a non-Essentials,
 * non-plan tenant pick up the whole slice. See
 * `compliance-w5-plan-matrix.test.ts` for the escalation-case regression
 * proof and `docs/source-of-truth/access-control-and-module-gating.md` for
 * why conversion into an Essentials plan, not package sniffing, is what
 * unlocks this.
 *
 * SECOND, kept as defence in depth: any one of `workforce_operations`,
 * `clinical_care`, or `financial_management` in the resolved package set
 * still disables every addition, not only the ones that package itself
 * implies — this only matters for a legacy/migrated tenant whose stored
 * `selectedPackages` override adds one of those wider packages onto an
 * Essentials plan; in that case the wider package's own PACKAGE_MODULES
 * entries already grant the equivalent routes directly, so this is a
 * redundant no-op, not a new restriction.
 */
function addComplianceCapabilityModules(
  target: Set<string>,
  resolvedPackages: ReadonlySet<string>,
  resolvedPlan: BillingPlanKey | null,
): void {
  if (!resolvedPlan || !COMPLIANCE_CAPABILITY_ESSENTIALS_PLAN_KEYS.has(resolvedPlan)) return;
  if (!resolvedPackages.has('compliance_essentials')) return;
  const hasWiderOperationalPackage = COMPLIANCE_CAPABILITY_WIDER_OPERATIONAL_PACKAGES.some((pkg) =>
    resolvedPackages.has(pkg),
  );
  if (hasWiderOperationalPackage) return;

  for (const definition of Object.values(COMPLIANCE_CAPABILITY_DEFINITIONS)) {
    for (const route of definition.routes) target.add(route);
  }
}

/**
 * Wave 5 slice 4 (2026-09-22, adversarial review item 4): strip the
 * deferred-until-lite-registers capability paths from an Essentials plan's
 * resolved module set, then re-grant only the staff-independent policy-library
 * children. The publication and assignment pages still call workforce-policy
 * projection/action RPCs and link to unavailable worker surfaces, so an
 * Essentials tenant with lite workers cannot honestly use them yet. Code,
 * conduct cases also require a login-linked staff projection, which lite
 * workers intentionally do not have. Paul enabled /me/compliance on 1 October
 * 2026: linked staff retain their page; staffless officers get People register
 * guidance without calling worker RPCs. Guarded by the exact same
 * closed plan-key set as `addComplianceCapabilityModules` — a no-op for
 * every other plan, so their resolved sets are completely unaffected
 * (`/people/policies-conduct` there still comes from their OWN
 * `workforce_operations`/`PACKAGE_MODULES.compliance_essentials` entries,
 * untouched by this function). See
 * ESSENTIALS_CAPABILITIES_DEFERRED_UNTIL_LITE_REGISTERS for the full
 * rationale per deferred path.
 */
function stripDeferredEssentialsCapabilities(
  target: Set<string>,
  resolvedPlan: BillingPlanKey | null,
): void {
  if (!resolvedPlan || !COMPLIANCE_CAPABILITY_ESSENTIALS_PLAN_KEYS.has(resolvedPlan)) return;
  for (const path of ESSENTIALS_CAPABILITIES_DEFERRED_UNTIL_LITE_REGISTERS) {
    target.delete(path);
  }
  target.add('/people/policies-conduct/governance');
  target.add('/people/policies-conduct/policies');
}

/**
 * Deliberate package marker for externally issued compliance-only trials.
 *
 * This is not a paid billing plan and must never be inferred from a legacy
 * `essentials` value. Only the fail-closed trial activation workflow stamps it.
 */
export const COMPLIANCE_TRIAL_PACKAGE_KEY = 'compliance_trial' as const;

/** Platform Services — always enabled regardless of plan.
 * NOTE: Do NOT add parent paths like /analytics here — the sidebar
 * matching logic uses prefix checks, so /analytics would enable ALL
 * /analytics/* children (board-pack, benchmarking, etc.). Instead,
 * add analytics paths to specific packages for proper tiering. */
export const PLATFORM_MODULES = [
  '/overview',
  '/activity',
  '/translate',
  '/admin/system/ai-usage',
  '/admin/system/master-data',
  '/admin/system/workflows',
  '/admin/system/scim',
  '/admin/system/sso',
  '/admin/system/tenant-config',
  '/audit/evidence',
  '/settings',
  '/settings/approvals',
  '/settings/billing',
  '/settings/assessments',
  '/settings/cert-allowance-registry',
  '/settings/cm-task-types',
  '/settings/data-management',
  '/settings/document-templates',
  '/settings/onboarding',
  '/settings/onboarding/client-packs',
  '/settings/onboarding/readiness',
  '/settings/onboarding-packs',
  '/settings/escalations',
  '/settings/funding-authorisations',
  '/settings/registration-providers',
  '/settings/facilities',
  '/settings/facility-config',
  '/settings/feature-flags',
  '/settings/holidays',
  '/settings/import',
  '/settings/modules',
  '/settings/notification-audit',
  '/settings/notification-preferences',
  '/settings/notification-routing',
  '/settings/notification-routing/wizard',
  '/settings/notifications',
  '/settings/notifications/log',
  '/settings/organisation',
  '/settings/portal',
  '/settings/retention',
  '/settings/roster',
  '/settings/security',
  '/settings/signature-templates',
  '/settings/system',
  '/settings/users',
  '/settings/webhooks',
  '/settings/webhooks/docs',
  '/settings/integrations/xero/wizard',
  '/settings/integrations/xero/backfill',
  '/settings/workflows',
  '/settings/workflows/closeout',
  '/help',
  '/help/tickets',
  '/tasks',
  '/tasks/all',
  '/tasks/new',
  '/tasks/signatures',
  '/workflows/instances',
  '/me/profile',
];

/**
 * Exact shell needed to operate compliance workflows during a guided trial.
 * Broad platform settings (billing, imports, integrations, modules, workflow
 * administration, payroll, workforce, and clinical configuration) are
 * intentionally absent.
 */
export const COMPLIANCE_TRIAL_SHELL_MODULES = [
  COMPLIANCE_TRIAL_SCOPE_MARKER,
  '/overview',
  '/settings',
  '/settings/document-templates',
  '/settings/facilities',
  '/settings/notification-audit',
  '/settings/notification-preferences',
  '/settings/notifications',
  '/settings/notifications/log',
  '/settings/organisation',
  '/settings/registration-providers',
  '/settings/retention',
  '/settings/security',
  '/settings/users',
  // Wave 5 slice 2 — pairs with the isComplianceTrialRouteAllowed allowlist
  // addition in compliance-trial-access.ts. Billing must be visible during
  // an active trial (not only after expiry) and reachable without a
  // redirect loop once expired, so a trial provider can convert to a paid
  // Essentials plan.
  '/settings/billing',
  '/help',
  '/help/tickets',
  '/tasks',
  '/tasks/all',
  '/me/profile',
] as const;

export const COMPLIANCE_TRIAL_MODULES = Array.from(
  new Set([...COMPLIANCE_TRIAL_SHELL_MODULES, ...(PACKAGE_MODULES.compliance_essentials ?? [])]),
).filter(
  (modulePath) =>
    modulePath === COMPLIANCE_TRIAL_SCOPE_MARKER || isComplianceTrialRouteAllowed(modulePath),
);

export const ALL_KNOWN_MODULE_PATHS = Array.from(
  new Set([...PLATFORM_MODULES, ...Object.values(PACKAGE_MODULES).flat()]),
).sort();

export const HR_WORKER_ONLY_GO_LIVE_SCOPE = 'hr_worker_only';
export const SAH_CORE_GO_LIVE_SCOPE = 'sah_core_go_live';
export const RAC_CORE_GO_LIVE_SCOPE = 'rac_core_go_live';

export const GO_LIVE_SCOPE_VALUES = [
  HR_WORKER_ONLY_GO_LIVE_SCOPE,
  SAH_CORE_GO_LIVE_SCOPE,
  RAC_CORE_GO_LIVE_SCOPE,
] as const;

export type GoLiveScopeKey = (typeof GO_LIVE_SCOPE_VALUES)[number];
export type VisibleGoLiveScope = GoLiveScopeKey | null;
export type LaunchProfileModuleStatus = 'core' | 'optional' | 'gated';

export type LaunchProfileDefinition = {
  key: GoLiveScopeKey;
  label: string;
  description: string;
  coreModules: readonly string[];
  optionalModules: readonly string[];
  defaultGatedModules: readonly string[];
};

export const HR_WORKER_ONLY_GO_LIVE_SHELL_MODULES = [
  '/overview',
  '/settings',
  '/settings/document-templates',
  '/settings/onboarding',
  '/settings/onboarding-packs',
  '/settings/notification-audit',
  '/settings/notification-preferences',
  '/settings/notifications',
  '/settings/organisation',
  '/settings/security',
  '/settings/users',
  '/help',
  '/help/tickets',
  '/tasks',
  '/tasks/all',
  '/tasks/new',
  '/tasks/signatures',
  '/workflows/instances',
  '/me/profile',
] as const;

export const HR_WORKER_ONLY_GO_LIVE_ENABLEABLE_MODULES = [
  '/staff',
  '/staff/training',
  '/compliance/screening',
  '/compliance/certifications',
  '/compliance/workforce',
  '/compliance/documents',
  '/compliance/documents/expiring',
  '/compliance/capability-framework',
  '/compliance/training-delivery',
  '/compliance/validity-rules',
  '/hr/onboarding',
  '/hr/compliance',
  '/hr/training',
  '/people/learning',
  '/people/learning/courses',
  '/people/learning/external-evidence',
  '/people/learning/competencies',
  '/people/development',
  '/people/development/performance',
  '/people/development/supervision',
  '/people/evidence',
  '/hr/performance',
  '/hr/policies',
  '/people/policies-conduct',
  '/hr/casual-conversion',
  '/hr/workforce-obligations',
  '/me/onboarding',
  '/me/policies',
  '/me/training',
  '/me/courses',
  '/me/performance',
  '/me/compliance',
  '/me/capability',
  '/me/contractor',
  '/me/hours',
  '/me/preferences',
  '/me/renewals',
] as const;

export const HR_WORKER_ONLY_GO_LIVE_MODULES = [
  ...HR_WORKER_ONLY_GO_LIVE_SHELL_MODULES,
  ...HR_WORKER_ONLY_GO_LIVE_ENABLEABLE_MODULES,
] as const;

const COMMON_GO_LIVE_CORE_MODULES = [
  '/overview',
  '/activity',
  '/clients',
  '/clients/new',
  '/staff',
  '/staff/training',
  '/compliance/incidents',
  '/compliance/open-disclosure',
  '/compliance/complaints',
  '/compliance/documents',
  '/compliance/documents/expiring',
  '/compliance/audit-pack',
  '/compliance/audit-pack/export',
  '/compliance/provider-obligations',
  '/compliance/restrictive-practices',
  '/compliance/screening',
  '/compliance/certifications',
  '/compliance/workforce',
  '/hr/onboarding',
  '/hr/compliance',
  '/hr/policies',
  '/people/policies-conduct',
  '/hr/training',
  '/people/learning',
  '/people/learning/courses',
  '/people/learning/external-evidence',
  '/people/learning/competencies',
  '/people/development',
  '/people/development/performance',
  '/people/development/supervision',
  '/people/evidence',
  '/me/profile',
  '/me/onboarding',
  '/me/compliance',
  '/me/credentials',
  '/me/hours',
  '/me/leave',
  '/me/policies',
  '/me/preferences',
  '/me/renewals',
  '/me/roster',
  '/me/shift-offers',
  '/me/shift-swaps',
  '/me/timesheets',
  '/me/messages',
  '/me/training',
  '/tasks',
  '/tasks/all',
  '/tasks/new',
  '/tasks/signatures',
  '/workflows/instances',
  '/help',
  '/help/tickets',
  '/settings',
  '/settings/document-templates',
  '/settings/facilities',
  '/settings/facility-config',
  '/settings/notification-audit',
  '/settings/notification-preferences',
  '/settings/notifications',
  '/settings/organisation',
  '/settings/security',
  '/settings/signature-templates',
  '/settings/system',
  '/settings/users',
] as const;

const GO_LIVE_GLOBAL_DEFAULT_GATED_MODULES = [
  '/admin/system/ai-usage',
  '/admin/system/master-data',
  '/admin/system/scim',
  '/admin/system/sso',
  '/admin/system/tenant-config',
  '/admin/system/workflows',
  '/analytics',
  '/analytics/benchmarking',
  '/analytics/board-pack',
  '/analytics/facilities',
  '/analytics/reports',
  '/care-pathways',
  '/care-plans/bulk-replan',
  '/care-plans/configuration',
  '/care-plans/synthesize',
  '/contractors',
  '/dashboards/executive',
  '/dashboards/iot',
  '/dashboards/multi-facility',
  '/dashboards/operations',
  '/dashboards/risk-correlation',
  '/head-office',
  '/hr/casual-conversion',
  '/hr/clinical-supervision',
  '/hr/offboarding',
  '/hr/performance',
  '/hr/performance/pips',
  '/me/casual-conversion',
  '/me/contractor',
  '/me/performance',
  '/me/supervision',
  '/finance',
  '/pricing/agreements',
  '/pricing/rate-cards',
  '/reports',
  '/reports/generations',
  '/reports/subscriptions',
  '/reports/workforce/recalls',
  '/roster/forecast',
  '/roster/health',
  '/roster/live',
  '/roster/optimise-day',
  '/roster/optimiser',
  '/roster/proposals',
  '/roster/recalls',
  '/roster/runtime',
  '/roster/sleepover-disturbances',
  '/roster/wing-staffing',
  '/settings/data-management',
  '/settings/feature-flags',
  '/settings/import',
  '/settings/integrations',
  '/settings/integrations/dhda',
  '/settings/integrations/hi',
  '/settings/integrations/services-australia',
  '/settings/integrations/xero/backfill',
  '/settings/modules',
  '/settings/webhooks',
  '/compliance/code-of-conduct/analytics',
  '/compliance/code-of-conduct/investigations',
  '/compliance/code-of-conduct/reports',
  '/compliance/environmental',
  '/compliance/ndis-overlay',
  '/compliance/training-delivery',
  '/compliance/validity-rules',
  '/compliance/whistleblower',
] as const;

const CORE_ROSTER_MODULES = [
  '/availability',
  '/me/availability',
  '/roster',
  '/roster/board',
  '/roster/dispatch',
  '/roster/grid',
  '/roster/handovers',
  '/roster/leave',
  '/roster/open-shifts',
  '/roster/periods',
  '/roster/import',
  '/roster/shift-swaps',
  '/roster/templates',
  '/roster/timesheets',
  '/roster/today',
  '/workforce/availability',
  '/workforce/capabilities',
  '/workforce/roster',
] as const;

const SAH_CORE_GO_LIVE_CORE_MODULES = [
  ...COMMON_GO_LIVE_CORE_MODULES,
  ...CORE_ROSTER_MODULES,
  '/assessments',
  '/care-plans',
  '/clients/*/sah',
  '/cm-tasks',
  '/cm-tasks/new',
  '/cm-tasks/approve',
  '/compliance/sah-evidence',
  '/dashboards/home-care-ops',
  '/finance/budgets/carry-over',
  '/finance/contributions',
  '/home-care',
  '/home-care/assessments',
  '/home-care/budget-explainer',
  '/home-care/budgets',
  '/home-care/funding-changes',
  '/home-care/classifications',
  '/home-care/claims',
  '/home-care/exceptions',
  '/home-care/readiness',
  '/home-care/reconciliation',
  '/home-care/schedules',
  '/home-care/service-areas',
  '/home-care/service-delivery',
  '/home-care/statements',
  '/reports/sah',
] as const;

const SAH_CORE_GO_LIVE_OPTIONAL_MODULES = [
  '/home-care/at-hm',
  '/settings/integrations/services-australia',
  '/finance/invoices',
  '/finance/claims',
  '/medications',
  '/medications/mar',
  '/medications/s8-register',
  '/monitoring',
  '/monitoring/devices',
  '/observations',
  '/telehealth',
  '/wounds',
] as const;

const SAH_CORE_GO_LIVE_GATED_MODULES = [
  ...GO_LIVE_GLOBAL_DEFAULT_GATED_MODULES,
  ...SAH_CORE_GO_LIVE_OPTIONAL_MODULES,
  '/advance-care-plans',
  '/clinical',
  '/clinical/pathways',
  '/clinical/restorative',
  '/clinical/supervision',
  '/compliance/prudential',
  '/compliance/rn-coverage',
  '/dashboards/don-clinical',
  '/dashboards/facility-ops',
  '/finance/accommodation',
  '/finance/an-acc',
  '/finance/residential-billing',
  '/finance/respite-billing',
  '/residential',
  '/residential/activities',
  '/residential/admissions',
  '/residential/adl-events',
  '/residential/care-minutes',
  '/residential/community',
  '/residential/daily-living',
  '/residential/dap-accrual',
  '/residential/meal-management',
  '/residential/night-rounds',
  '/residential/occupancy',
  '/residential/rooms',
  '/residential/rounds',
  '/residential/visitors',
] as const;

const RAC_CORE_GO_LIVE_CORE_MODULES = [
  ...COMMON_GO_LIVE_CORE_MODULES,
  ...CORE_ROSTER_MODULES,
  '/assessments',
  '/care-plans',
  '/compliance/ipc',
  '/compliance/rn-coverage',
  '/dashboards/facility-ops',
  '/dashboards/quality',
  '/dashboards/workforce',
  '/finance/an-acc',
  '/finance/residential-billing',
  '/residential',
  '/residential/activities',
  '/residential/admissions',
  '/residential/adl-events',
  '/residential/care-minutes',
  '/residential/care-minutes/an-acc',
  '/residential/care-minutes/daily',
  '/residential/community',
  '/residential/daily-living',
  '/residential/meal-management',
  '/residential/night-rounds',
  '/residential/occupancy',
  '/residential/rooms',
  '/residential/rounds',
  '/residential/visitors',
] as const;

const RAC_CORE_GO_LIVE_OPTIONAL_MODULES = [
  '/advance-care-plans',
  '/clinical',
  '/clinical/alerts',
  '/clinical/handovers',
  '/clinical/pathways',
  '/clinical/restorative',
  '/clinical/supervision',
  '/compliance/clinical-governance',
  '/compliance/prudential',
  '/compliance/prudential/deposits',
  '/finance/accommodation',
  '/finance/respite-billing',
  '/medications',
  '/medications/mar',
  '/medications/s8-register',
  '/monitoring',
  '/monitoring/devices',
  '/observations',
  '/payroll',
  '/payroll/readiness',
  '/payroll/manual-closures',
  '/payroll/provider-handoff',
  '/payroll/fixed-pay',
  '/payroll/corrections',
  '/payroll/policy',
  '/payroll/pay-runs',
  '/payroll/queue',
  '/payroll/reconciliation',
  '/payroll/timesheet',
  '/payroll/award-review',
  '/residential/dap-accrual',
  '/settings/integrations/xero',
  '/settings/integrations/xero/wizard',
  '/settings/payroll/aba-sender',
  '/settings/payroll/eba',
  '/settings/payroll/pay-roles',
  '/telehealth',
  '/wounds',
] as const;

const RAC_CORE_GO_LIVE_GATED_MODULES = [
  ...GO_LIVE_GLOBAL_DEFAULT_GATED_MODULES,
  ...RAC_CORE_GO_LIVE_OPTIONAL_MODULES,
  '/clients/*/sah',
  '/cm-tasks',
  '/cm-tasks/new',
  '/cm-tasks/approve',
  '/compliance/sah-evidence',
  '/settings/integrations/services-australia',
  '/dashboards/home-care-ops',
  '/finance/budgets/carry-over',
  '/finance/contributions',
  '/home-care',
  '/home-care/assessments',
  '/home-care/at-hm',
  '/home-care/budget-explainer',
  '/home-care/budgets',
  '/home-care/funding-changes',
  '/home-care/classifications',
  '/home-care/claims',
  '/home-care/exceptions',
  '/home-care/readiness',
  '/home-care/reconciliation',
  '/home-care/schedules',
  '/home-care/service-areas',
  '/home-care/service-delivery',
  '/home-care/statements',
  '/reports/sah',
] as const;

export const LAUNCH_PROFILE_DEFINITIONS = {
  [HR_WORKER_ONLY_GO_LIVE_SCOPE]: {
    key: HR_WORKER_ONLY_GO_LIVE_SCOPE,
    label: 'HR worker preview',
    description:
      'Minimal worker onboarding and compliance preview. Not suitable for operational SAH or RAC go-live.',
    coreModules: HR_WORKER_ONLY_GO_LIVE_SHELL_MODULES,
    optionalModules: HR_WORKER_ONLY_GO_LIVE_ENABLEABLE_MODULES,
    defaultGatedModules: [],
  },
  [SAH_CORE_GO_LIVE_SCOPE]: {
    key: SAH_CORE_GO_LIVE_SCOPE,
    label: 'SAH core go-live',
    description:
      'Support at Home operations: participants, care plans, schedules, delivery, budgets, claims, statements, workforce, and essential settings.',
    coreModules: SAH_CORE_GO_LIVE_CORE_MODULES,
    optionalModules: SAH_CORE_GO_LIVE_OPTIONAL_MODULES,
    defaultGatedModules: SAH_CORE_GO_LIVE_GATED_MODULES,
  },
  [RAC_CORE_GO_LIVE_SCOPE]: {
    key: RAC_CORE_GO_LIVE_SCOPE,
    label: 'RAC core go-live',
    description:
      'Residential operations: residents, admissions, rooms, occupancy, care minutes, daily ops, roster, payroll readiness, core clinical, compliance, and essential settings.',
    coreModules: RAC_CORE_GO_LIVE_CORE_MODULES,
    optionalModules: RAC_CORE_GO_LIVE_OPTIONAL_MODULES,
    defaultGatedModules: RAC_CORE_GO_LIVE_GATED_MODULES,
  },
} satisfies Record<GoLiveScopeKey, LaunchProfileDefinition>;

export const LAUNCH_PROFILE_MODULE_BOUNDARY_PATHS = Array.from(
  new Set(
    Object.values(LAUNCH_PROFILE_DEFINITIONS).flatMap((profile) => [
      ...profile.coreModules,
      ...profile.optionalModules,
      ...profile.defaultGatedModules,
    ]),
  ),
).sort();

export const ALL_MODULE_BOUNDARY_PATHS = Array.from(
  new Set([...ALL_KNOWN_MODULE_PATHS, ...LAUNCH_PROFILE_MODULE_BOUNDARY_PATHS]),
).sort();

export type TenantModuleActivationSummary = {
  module_key?: string | null;
  activation_state?: string | null;
};

export const CONTROLLED_MODULE_ROUTE_PATHS: Record<string, string[]> = {
  sah_service_delivery: [
    '/home-care/service-areas',
    '/home-care/schedules',
    '/home-care/classifications',
    '/home-care/service-delivery',
    '/home-care/exceptions',
    '/home-care/reconciliation',
    '/dashboards/home-care-ops',
  ],
  sah_care_management: [
    '/home-care/assessments',
    '/cm-tasks',
    '/cm-tasks/new',
    '/cm-tasks/approve',
    '/cm-tasks/unknown',
  ],
  sah_budget_claims: [
    '/home-care/budgets',
    '/home-care/funding-changes',
    '/home-care/budget-explainer',
    '/home-care/readiness',
    '/home-care/claims',
    '/compliance/sah-evidence',
    '/finance/contributions',
    '/finance/budgets/carry-over',
  ],
  sah_monthly_statements: ['/home-care/statements'],
  sah_at_hm: ['/home-care/at-hm'],
  rac_admissions: ['/residential/admissions'],
  rac_accommodation: ['/residential/rooms', '/residential/occupancy'],
  rac_care_minutes: [
    '/residential/care-minutes',
    '/residential/care-minutes/an-acc',
    '/residential/care-minutes/daily',
  ],
  rac_daily_operations: [
    '/residential',
    '/residential/activities',
    '/residential/visitors',
    '/residential/community',
    '/residential/night-rounds',
    '/residential/meal-management',
    '/residential/daily-living',
    '/residential/adl-events',
    '/residential/rounds',
  ],
};

/* ── Helpers ───────────────────────────────────────────────── */

export function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return normalizeStringArray(parsed);
    } catch {
      return [trimmed];
    }
  }
  return [];
}

export function normalizeSelectedPackages(value: unknown): string[] {
  return normalizeStringArray(value).map((pkg) => LEGACY_PACKAGE_ALIAS[pkg] ?? pkg);
}

export function isComplianceTrialPackageSelection(value: unknown): boolean {
  const packages = normalizeSelectedPackages(value);
  return packages.length === 1 && packages[0] === COMPLIANCE_TRIAL_PACKAGE_KEY;
}

function addPackageModules(target: Set<string>, packages: unknown = []) {
  const allPackages = new Set<string>(normalizeSelectedPackages(packages));
  allPackages.add('compliance_essentials');

  for (const pkg of allPackages) {
    const pkgModules = PACKAGE_MODULES[pkg];
    if (pkgModules) pkgModules.forEach((modulePath) => target.add(modulePath));
  }
}

/**
 * Resolve a potentially legacy plan key to a current BillingPlanKey.
 */
export function resolvePlanKey(key: string): BillingPlanKey | null {
  if (key in BILLING_PLANS) return key as BillingPlanKey;
  if (key in LEGACY_PLAN_MAP) return LEGACY_PLAN_MAP[key] ?? null;
  return null;
}

export function resolveEffectiveBillingPlan(input: {
  billingPlan?: string | null;
  subscriptionTier?: string | null;
  legacyPlan?: string | null;
}): BillingPlanKey | null {
  const direct = input.billingPlan ? resolvePlanKey(input.billingPlan) : null;
  if (direct) return direct;

  const tier = input.subscriptionTier?.toLowerCase();
  if (tier && TIER_TO_BILLING_PLAN[tier]) return TIER_TO_BILLING_PLAN[tier] ?? null;

  const legacy = input.legacyPlan?.toLowerCase();
  if (legacy && TIER_TO_BILLING_PLAN[legacy]) return TIER_TO_BILLING_PLAN[legacy] ?? null;
  return input.legacyPlan ? resolvePlanKey(input.legacyPlan) : null;
}

/**
 * Get all enabled module paths for a billing plan.
 *
 * The new tier model is fixed — each plan has a predetermined set of
 * packages. The selectedPackages parameter is kept only for legacy
 * backward compatibility during migration.
 */
export function getEnabledModules(
  plan: BillingPlanKey | null,
  selectedPackages?: unknown,
): string[] | null {
  // Enterprise/Complete plans now resolve to an explicit list. `null = all`
  // masked unfinished/quarantined modules and is not safe for production
  // onboarding or staged module release.
  if (plan && ((plan as string).includes('enterprise') || (plan as string).includes('complete'))) {
    return Array.from(new Set(ALL_KNOWN_MODULE_PATHS));
  }

  // Resolve legacy plan keys
  const resolvedPlan = plan ? resolvePlanKey(plan as string) : null;
  // This fixed compliance-only product cannot inherit an old operational bundle.
  if (resolvedPlan === 'home_care_compliance') selectedPackages = [];

  // No plan — check for legacy selectedPackages or grant all (admin-created)
  // R1 HIGH (2026-09-21): a null plan is never an Essentials plan, so it
  // never qualifies for the Wave 5 additive slice regardless of package
  // shape — addComplianceCapabilityModules enforces that itself, but this
  // branch has no `resolvedPlan` to pass it in the first place.
  if (!resolvedPlan) {
    const packages = normalizeSelectedPackages(selectedPackages);
    if (packages.length > 0) {
      const modules = new Set<string>(PLATFORM_MODULES);
      addPackageModules(modules, packages);
      return Array.from(modules);
    }
    return Array.from(new Set(ALL_KNOWN_MODULE_PATHS)); // legacy/admin-created, explicit all
  }

  const planDef = BILLING_PLANS[resolvedPlan];
  if (!planDef) return Array.from(new Set(ALL_KNOWN_MODULE_PATHS));

  // Build module set from the plan's fixed packages
  const modules = new Set<string>(PLATFORM_MODULES);
  addPackageModules(modules, planDef.packages);
  // Preserve legacy mixed-tenant entitlements that were stored as package
  // selections rather than a single canonical billing plan.
  addPackageModules(modules, selectedPackages ?? []);
  addComplianceCapabilityModules(
    modules,
    new Set([
      ...planDef.packages,
      ...normalizeSelectedPackages(selectedPackages ?? []),
      'compliance_essentials',
    ]),
    resolvedPlan,
  );
  stripDeferredEssentialsCapabilities(modules, resolvedPlan);
  addComplianceOnlyPlanModules(modules, resolvedPlan);

  return applyHomeCareComplianceCeiling(Array.from(modules), resolvedPlan);
}

function moduleOrgTypePatternMatches(modulePath: string, pattern: string): boolean {
  const pathSegments = modulePath.split('/').filter(Boolean);
  const patternSegments = pattern.split('/').filter(Boolean);
  if (pathSegments.length < patternSegments.length) return false;
  return patternSegments.every(
    (segment, index) => segment === '*' || segment === pathSegments[index],
  );
}

function matchesOrgTypeModulePrefix(modulePath: string, prefix: string): boolean {
  return (
    modulePath === prefix ||
    modulePath.startsWith(prefix + '/') ||
    moduleOrgTypePatternMatches(modulePath, prefix)
  );
}

function isResidentialOnlyModulePath(modulePath: string): boolean {
  // FPS Part 2 includes category 4/5 home providers. Compatibility cannot depend
  // on the operational family; purchased entitlement and leaf authority remain.
  if (matchesOrgTypeModulePrefix(modulePath, '/compliance/prudential')) {
    return false;
  }
  return RESIDENTIAL_ONLY_MODULE_PREFIXES.some((prefix) =>
    matchesOrgTypeModulePrefix(modulePath, prefix),
  );
}

function isHomeCareOnlyModulePath(modulePath: string): boolean {
  return HOME_CARE_ONLY_MODULE_PREFIXES.some((prefix) =>
    matchesOrgTypeModulePrefix(modulePath, prefix),
  );
}

/** Whether a module path is relevant for the tenant organisation type. */
export function isModulePathVisibleForOrgType(
  modulePath: string,
  orgType: ModuleOrgType | null | undefined,
): boolean {
  if (!orgType || orgType === 'MIXED') return true;
  if (orgType === 'HOME_CARE') return !isResidentialOnlyModulePath(modulePath);
  if (orgType === 'RESIDENTIAL') return !isHomeCareOnlyModulePath(modulePath);
  return true;
}

export function filterModulesByOrgType(
  modules: string[],
  orgType: ModuleOrgType | null | undefined,
): string[] {
  if (!orgType || orgType === 'MIXED') return [...modules];

  if (orgType === 'HOME_CARE') {
    return modules.filter((modulePath) => !isResidentialOnlyModulePath(modulePath));
  }

  if (orgType === 'RESIDENTIAL') {
    return modules.filter((modulePath) => !isHomeCareOnlyModulePath(modulePath));
  }

  return [...modules];
}

export function getResolvedEnabledModules(
  plan: BillingPlanKey | null,
  selectedPackages?: unknown,
): string[] | null {
  const resolvedPlan = plan ? resolvePlanKey(plan as string) : null;
  // This fixed compliance-only product cannot inherit an old operational bundle.
  if (resolvedPlan === 'home_care_compliance') selectedPackages = [];

  // R1 HIGH (2026-09-21): a null plan (package-only/legacy/admin-created
  // tenant) is never an Essentials plan and gets nothing from the Wave 5
  // additive slice, regardless of package shape — see
  // addComplianceCapabilityModules.
  if (!resolvedPlan) {
    const packages = normalizeSelectedPackages(selectedPackages);
    if (packages.length === 0) return Array.from(new Set(ALL_KNOWN_MODULE_PATHS));

    const modules = new Set<string>(PLATFORM_MODULES);
    addPackageModules(modules, packages);
    return Array.from(modules);
  }

  const planDef = BILLING_PLANS[resolvedPlan];
  if (!planDef) return Array.from(new Set(ALL_KNOWN_MODULE_PATHS));

  const modules = new Set<string>(PLATFORM_MODULES);
  addPackageModules(modules, planDef.packages);
  // Mixed and migrated tenants can carry additional package entitlements that
  // are not implied by the stored billing plan alone.
  addPackageModules(modules, selectedPackages ?? []);
  addComplianceCapabilityModules(
    modules,
    new Set([
      ...planDef.packages,
      ...normalizeSelectedPackages(selectedPackages ?? []),
      'compliance_essentials',
    ]),
    resolvedPlan,
  );
  stripDeferredEssentialsCapabilities(modules, resolvedPlan);
  addComplianceOnlyPlanModules(modules, resolvedPlan);
  return applyHomeCareComplianceCeiling(Array.from(modules), resolvedPlan);
}

export function applyModuleOverrides(
  baseModules: string[] | null,
  options: {
    orgType?: ModuleOrgType | null;
    systemEnabledModules?: unknown;
    adminDisabledModules?: unknown;
  } = {},
): string[] | null {
  const forcedEnabled = filterModulesByOrgType(
    [...new Set(normalizeStringArray(options.systemEnabledModules))],
    options.orgType,
  );
  const disabled = new Set(
    [...new Set(normalizeStringArray(options.adminDisabledModules))].filter(
      (modulePath) => !forcedEnabled.includes(modulePath),
    ),
  );

  if (!baseModules) {
    const explicitAll = filterModulesByOrgType(ALL_KNOWN_MODULE_PATHS, options.orgType);
    const merged = new Set(explicitAll);
    for (const modulePath of forcedEnabled) merged.add(modulePath);
    for (const modulePath of disabled) merged.delete(modulePath);
    return Array.from(merged);
  }

  const merged = new Set(filterModulesByOrgType(baseModules, options.orgType));
  for (const modulePath of forcedEnabled) merged.add(modulePath);
  for (const modulePath of disabled) merged.delete(modulePath);
  return Array.from(merged);
}

export function applyTenantModuleActivations(
  baseModules: string[] | null,
  _activations: TenantModuleActivationSummary[] | null | undefined,
): string[] | null {
  // Module activation rows are readiness/status metadata only. Hard access is
  // governed by billing packages, org type, role gates, and explicit admin
  // disables so real provider tenants are not locked into preview go-live slices.
  return baseModules;
}

export function isHrWorkerOnlyGoLiveScope(scope: unknown): boolean {
  return normalizeGoLiveScope(scope) === HR_WORKER_ONLY_GO_LIVE_SCOPE;
}

export function normalizeGoLiveScope(scope: unknown): VisibleGoLiveScope {
  if (typeof scope !== 'string') return null;
  const normalized = scope.toLowerCase();
  return GO_LIVE_SCOPE_VALUES.includes(normalized as GoLiveScopeKey)
    ? (normalized as GoLiveScopeKey)
    : null;
}

export function getLaunchProfileDefinition(scope: unknown): LaunchProfileDefinition | null {
  const normalized = normalizeGoLiveScope(scope);
  return normalized ? LAUNCH_PROFILE_DEFINITIONS[normalized] : null;
}

export function getLaunchProfileModuleLedger(scope: unknown): Array<{
  modulePath: string;
  status: LaunchProfileModuleStatus;
  profile: GoLiveScopeKey;
}> {
  const profile = getLaunchProfileDefinition(scope);
  if (!profile) return [];

  const ledger = new Map<string, LaunchProfileModuleStatus>();
  for (const modulePath of profile.coreModules) ledger.set(modulePath, 'core');
  for (const modulePath of profile.optionalModules) {
    if (!ledger.has(modulePath)) ledger.set(modulePath, 'optional');
  }
  for (const modulePath of profile.defaultGatedModules) {
    if (!ledger.has(modulePath)) ledger.set(modulePath, 'gated');
  }

  return Array.from(ledger.entries())
    .map(([modulePath, status]) => ({ modulePath, status, profile: profile.key }))
    .sort((a, b) => a.status.localeCompare(b.status) || a.modulePath.localeCompare(b.modulePath));
}

export function applyGoLiveScopeModules(
  baseModules: string[] | null,
  _goLiveScope?: unknown,
  _systemEnabledModules?: unknown,
  _orgType?: ModuleOrgType | null,
): string[] | null {
  // Legacy launch profiles remain available for setup/readiness display, but
  // no longer narrow the actual module set. Access should not depend on a
  // customer-specific go-live preview profile.
  return baseModules;
}

export function computeTenantEnabledModules(options: {
  billingPlan: BillingPlanKey | null;
  /**
   * Set only after evaluating the full authoritative trial predicate
   * (plan/status/demo/package/marker). Package selection alone is deliberately
   * insufficient because malformed paid tenants must retain their paid-tier
   * entitlements.
   */
  complianceTrial?: boolean;
  selectedPackages?: unknown;
  orgType?: ModuleOrgType | null;
  systemEnabledModules?: unknown;
  adminDisabledModules?: unknown;
  legacyEnabledModules?: unknown;
  moduleActivations?: TenantModuleActivationSummary[] | null;
  goLiveScope?: unknown;
}): string[] | null {
  if (options.complianceTrial === true) {
    // Trial scope is a closed contract: neither legacy enabledModules nor
    // tenant/system overrides may widen it. Org-type filtering can only remove
    // irrelevant paths.
    return filterModulesByOrgType([...COMPLIANCE_TRIAL_MODULES], options.orgType);
  }

  const baseModules =
    getResolvedEnabledModules(options.billingPlan, options.selectedPackages ?? []) ??
    (options.legacyEnabledModules ? normalizeStringArray(options.legacyEnabledModules) : null);

  const modules = applyModuleOverrides(baseModules, {
    orgType: options.orgType,
    systemEnabledModules: options.systemEnabledModules,
    adminDisabledModules: options.adminDisabledModules,
  });
  const activatedModules = applyTenantModuleActivations(
    applyGoLiveScopeModules(
      modules,
      options.goLiveScope,
      options.systemEnabledModules,
      options.orgType,
    ),
    options.moduleActivations,
  );
  // This product has a closed entitlement ceiling, including after legacy and
  // system overrides. Keep the existing override precedence for every other plan.
  if (options.billingPlan === 'home_care_compliance') {
    const allowed = new Set(getResolvedEnabledModules('home_care_compliance') ?? []);
    return [
      ...new Set((activatedModules ?? []).filter((modulePath) => allowed.has(modulePath))),
    ].filter(
      (modulePath) =>
        !['/settings/payroll', '/settings/roster', '/payroll', '/roster'].some(
          (path) => modulePath === path || modulePath.startsWith(`${path}/`),
        ),
    );
  }
  return activatedModules;
}

/** Check if subscription is in a billable state.
 *  Phase 5.7 P1-C — `trial_expired` is NOT active.  Once a trial expires
 *  without a connected Stripe subscription, the tenant is gated. */
export function isSubscriptionActive(status: SubscriptionStatus): boolean {
  return status === 'active' || status === 'trialing';
}

/** Check if subscription needs attention (soft enforcement) */
export function isSubscriptionWarning(status: SubscriptionStatus): boolean {
  return status === 'past_due' || status === 'incomplete' || status === 'unpaid';
}

/** Check if subscription is fully blocked */
export function isSubscriptionBlocked(status: SubscriptionStatus): boolean {
  return status === 'canceled' || status === 'suspended' || status === 'trial_expired';
}

/** Calculate monthly cost in cents */
export function calculateMonthlyCost(
  plan: BillingPlanKey,
  interval: BillingInterval,
  quantity: number,
): number {
  const planDef = BILLING_PLANS[plan];
  const unitPrice = interval === 'annual' ? planDef.annualPriceCents : planDef.monthlyPriceCents;
  // eslint-disable-next-line statura/no-floating-point-money -- library helper; precision bounded by scaled integer inputs or round2 at return
  return unitPrice * quantity;
}

/** Format cents to AUD display string */
export function formatAud(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/** Volume discount rate based on total units */
export function getVolumeDiscount(totalUnits: number): number {
  if (totalUnits >= 1000) return 0.2;
  if (totalUnits >= 500) return 0.15;
  if (totalUnits >= 200) return 0.1;
  return 0;
}

/** Days remaining in trial */
export function trialDaysRemaining(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null;
  const now = new Date();
  const end = new Date(trialEndsAt);
  const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

/** Map Stripe subscription status to our status enum */
export function mapStripeStatus(stripeStatus: string): SubscriptionStatus {
  switch (stripeStatus) {
    case 'trialing':
      return 'trialing';
    case 'active':
      return 'active';
    case 'past_due':
      return 'past_due';
    case 'canceled':
      return 'canceled';
    case 'incomplete':
      return 'incomplete';
    case 'incomplete_expired':
      return 'canceled';
    case 'unpaid':
      return 'unpaid';
    case 'paused':
      return 'suspended';
    default:
      return 'suspended';
  }
}
